/**
 * React Hook for Socket.IO Real-Time Communication
 * Handles connection, authentication, and event management
 */

import { useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { getAuthSession } from '../services/api';

let globalSocket = null;

/**
 * useSocket Hook - Connect to Socket.IO server
 * @returns {Object} Socket instance and utility methods
 */
export const useSocket = () => {
  const socketRef = useRef(null);
  const authSession = getAuthSession();

  useEffect(() => {
    // Only connect if not already connected
    if (globalSocket && globalSocket.connected) {
      socketRef.current = globalSocket;
      return;
    }

    // Don't connect if not authenticated
    if (!authSession?.token) {
      console.log('⚠️ Socket: Skipping connection - not authenticated');
      return;
    }

    try {
      // Connect to Socket.IO server with authentication
      const socketUrl = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || (typeof window !== 'undefined' && window.location.origin ? window.location.origin : '');
      const socket = io(socketUrl, {
        auth: {
          token: authSession.token
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        transports: ['websocket', 'polling']
      });

      // Connection events
      socket.on('connect', () => {
        console.log('✅ Socket.IO connected:', socket.id);
      });

      socket.on('disconnect', () => {
        console.log('❌ Socket.IO disconnected');
      });

      socket.on('connect_error', (error) => {
        console.error('❌ Socket.IO connection error:', error);
      });

      socket.on('error', (error) => {
        console.error('❌ Socket.IO error:', error);
      });

      socketRef.current = socket;
      globalSocket = socket;
    } catch (error) {
      console.error('❌ Failed to connect Socket.IO:', error);
    }

    // Cleanup on unmount
    return () => {
      // Don't disconnect global socket - keep it alive for other components
    };
  }, [authSession?.token]);

  // Subscribe to chat events
  const subscribeToChat = useCallback((chatId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('chat:join', { chatId });
      console.log(`📝 Subscribed to chat: ${chatId}`);
    }
  }, []);

  // Send chat message
  const sendChatMessage = useCallback((chatId, message, senderId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('chat:message', {
        chatId,
        message,
        senderId
      });
      console.log(`💬 Message sent to ${chatId}`);
    } else {
      console.warn('⚠️ Socket not connected, message not sent');
    }
  }, []);

  // Listen for new messages
  const onChatMessage = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('chat:message:new', callback);
    }
  }, []);

  // Send typing indicator
  const sendTypingIndicator = useCallback((chatId, userId, isTyping) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('chat:typing', { chatId, userId, isTyping });
    }
  }, []);

  // Listen for typing indicators
  const onTypingIndicator = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('chat:typing-indicator', callback);
    }
  }, []);

  // Mark messages as read
  const markMessagesAsRead = useCallback((chatId, messageIds) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('chat:message-read', { chatId, messageIds });
    }
  }, []);

  // Listen for read receipts
  const onMessagesRead = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('chat:messages-read', callback);
    }
  }, []);

  // Subscribe to notifications
  const subscribeToNotifications = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('notification:subscribe');
      console.log('🔔 Subscribed to notifications');
    }
  }, []);

  // Listen for new notifications
  const onNotification = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('notification:new', callback);
    }
  }, []);

  // Listen for user online status
  const onUserOnline = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('user:online', callback);
    }
  }, []);

  // Listen for user offline status
  const onUserOffline = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('user:offline', callback);
    }
  }, []);

  // Set user status
  const setUserStatus = useCallback((status) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('user:status', { status });
    }
  }, []);

  // Unsubscribe from chat
  const unsubscribeFromChat = useCallback((chatId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('chat:leave', { chatId });
    }
  }, []);

  // ==================== WORKSHEET COLLABORATION HELPERS ====================
  const joinWorksheet = useCallback((worksheetId, userName, userRole) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('worksheet:join', { worksheetId, userName, userRole });
    }
  }, []);

  const leaveWorksheet = useCallback((worksheetId, userName) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('worksheet:leave', { worksheetId, userName });
    }
  }, []);

  const sendWorksheetCellChange = useCallback((worksheetId, rowIndex, colIndex, value, userName, userRole) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('worksheet:cell_change', {
        worksheetId,
        rowIndex,
        colIndex,
        value,
        userName,
        userRole,
      });
    }
  }, []);

  const sendWorksheetSync = useCallback((worksheetId, sheetData, clientResponses, userName, userRole) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('worksheet:sync_data', {
        worksheetId,
        sheetData,
        clientResponses,
        userName,
        userRole,
      });
    }
  }, []);

  const onWorksheetCellUpdate = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('worksheet:cell_updated', callback);
    }
  }, []);

  const onWorksheetDataSynced = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('worksheet:data_synced', callback);
    }
  }, []);

  const onWorksheetUserJoined = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('worksheet:user_joined', callback);
    }
  }, []);

  const onWorksheetUserLeft = useCallback((callback) => {
    if (socketRef.current) {
      socketRef.current.on('worksheet:user_left', callback);
    }
  }, []);

  return {
    socket: socketRef.current,
    isConnected: socketRef.current?.connected || false,
    // Chat methods
    subscribeToChat,
    sendChatMessage,
    onChatMessage,
    sendTypingIndicator,
    onTypingIndicator,
    markMessagesAsRead,
    onMessagesRead,
    unsubscribeFromChat,
    // Notification methods
    subscribeToNotifications,
    onNotification,
    // User status methods
    onUserOnline,
    onUserOffline,
    setUserStatus,
    // Worksheet collaboration methods
    joinWorksheet,
    leaveWorksheet,
    sendWorksheetCellChange,
    sendWorksheetSync,
    onWorksheetCellUpdate,
    onWorksheetDataSynced,
    onWorksheetUserJoined,
    onWorksheetUserLeft,
  };
};

export default useSocket;
